const envName = import.meta.env.VITE_ENV_NAME;
const API_BASE_URL =
  envName === "dev" || envName === "staging" || envName === "prod"
    ? (import.meta.env.VITE_API_URL ?? "")
    : "";

export class AdminApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;

  constructor(status: number, statusText: string, body: unknown) {
    const message =
      typeof body === "object" && body !== null && "message" in body
        ? String((body as Record<string, unknown>).message)
        : `${status} ${statusText}`;
    super(message);
    this.name = "AdminApiError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

export async function adminFetch<T>(config: {
  url: string;
  method: string;
  params?: Record<string, string | number | boolean | undefined>;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<T> {
  const { url, method, params, data, headers, signal } = config;

  let fullUrl = `${API_BASE_URL}${url}`;
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    }
    fullUrl = `${fullUrl}?${searchParams}`;
  }

  const token = await getToken();

  const response = await fetch(fullUrl, {
    method,
    headers: {
      "Content-Type": "application/json",
      "x-client-app": "sonify-music",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: data ? JSON.stringify(data) : undefined,
    signal,
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    throw new AdminApiError(response.status, response.statusText, body);
  }

  return response.json() as Promise<T>;
}

/**
 * Like {@link adminFetch} but returns the raw streaming `Response` (with the
 * admin Bearer + headers attached) for SSE / fetch-streaming endpoints —
 * `EventSource` can't carry the Authorization header, but `fetch` can.
 */
export async function adminStream(config: {
  url: string;
  method?: string;
  data?: unknown;
  signal?: AbortSignal;
}): Promise<Response> {
  const { url, method = "POST", data, signal } = config;
  const token = await getToken();
  const response = await fetch(`${API_BASE_URL}${url}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "x-client-app": "sonify-music",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: data ? JSON.stringify(data) : undefined,
    signal,
  });
  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    throw new AdminApiError(response.status, response.statusText, body);
  }
  return response;
}

let getToken: () => Promise<string | null> = async () => null;

export function setTokenGetter(getter: () => Promise<string | null>) {
  getToken = getter;
}
